import { CheckOutlined, DownOutlined, GlobalOutlined, LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Input, Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export const VOTE_GRID_PAGE_SIZE = 15;

const SUGGESTION_MAX = 200;

/** Prefix giá trị option AutoComplete để phân biệt chọn từ dropdown (tránh trùng tên) */
const VOTE_AC_PREFIX = '__vote__';

/** Lọc catalog đội/cầu thủ theo quốc gia (cùng logic bộ lọc trong Home) */
function filterCatalogByCountry(items, countryFilter, catalogKind) {
  if (!items?.length) return [];
  if (countryFilter === 'all') return items;
  if (catalogKind === 'team') {
    return items.filter((t) => {
      const code =
        t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
      return code === countryFilter;
    });
  }
  return items.filter((p) => String(p.countryCode || '').toLowerCase() === countryFilter);
}

function filterCatalogByQuery(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((p) => p.name.toLowerCase().includes(q));
}

/** Trả về mảng số trang hoặc 'ellipsis' cho thanh phân trang dạng 1 2 3 … 23 */
function getPaginationItems(current, lastPage) {
  if (lastPage <= 1) return [1];
  if (lastPage <= 9) {
    return Array.from({ length: lastPage }, (_, i) => i + 1);
  }

  const pages = [];
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(lastPage - 1, current + 1);

  pages.push(1);

  if (current <= 5) {
    for (let p = 2; p <= Math.min(5, lastPage - 1); p += 1) pages.push(p);
    if (lastPage > 6) pages.push('ellipsis');
  } else if (current >= lastPage - 3) {
    pages.push('ellipsis');
    for (let p = Math.max(2, lastPage - 4); p <= lastPage - 1; p += 1) pages.push(p);
  } else {
    if (windowStart > 2) pages.push('ellipsis');
    for (let p = windowStart; p <= windowEnd; p += 1) pages.push(p);
    if (windowEnd < lastPage - 1) pages.push('ellipsis');
  }

  if (lastPage > 1) pages.push(lastPage);

  const deduped = [];
  let prev = null;
  for (const p of pages) {
    if (p === 'ellipsis') {
      if (deduped[deduped.length - 1] !== 'ellipsis') deduped.push(p);
      continue;
    }
    if (p === prev) continue;
    if (typeof p === 'number' && deduped.includes(p)) continue;
    deduped.push(p);
    prev = p;
  }
  return deduped;
}

export function VoteGridPagination({ current, totalItems, pageSize, onChange, className }) {
  const lastPage = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(1, current), lastPage);
  const items = useMemo(() => getPaginationItems(safePage, lastPage), [safePage, lastPage]);

  if (totalItems <= 0) return null;

  return (
    <div
      className={twMerge(
        'mx-auto grid w-full max-w-[min(100%,420px)] sm:max-w-[min(100%,520px)] grid-cols-[1fr_auto_1fr] items-center gap-x-1 gap-y-0.5 rounded-full bg-[#f1f5f9] px-2 py-1 sm:px-2.5 sm:py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]',
        className
      )}
      role="navigation"
      aria-label="Phân trang danh sách"
    >
      <div className="flex min-w-0 justify-start">
        <button
          type="button"
          onClick={() => onChange(safePage - 1)}
          disabled={safePage <= 1}
          className={twMerge(
            'flex shrink-0 items-center gap-0.5 rounded-full border px-2 py-0.5 text-[12px] sm:text-[13px] font-semibold transition-colors leading-none',
            safePage <= 1
              ? 'pointer-events-none cursor-not-allowed border-transparent text-[#94a3b8] opacity-45'
              : 'border-[#0253e0] bg-white text-[#0253e0] hover:bg-[#e8f1ff]'
          )}
        >
          <LeftOutlined className="text-[11px] sm:text-[12px]" />
          Trước
        </button>
      </div>
      <div className="flex min-w-0 max-w-full flex-wrap items-center justify-center gap-1 sm:gap-1.5">
        {items.map((entry, idx) =>
          entry === 'ellipsis' ? (
            <span key={`e-${idx}`} className="px-1 text-[15px] sm:text-[17px] font-medium text-[#64748b] select-none leading-none">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => onChange(entry)}
              className={twMerge(
                'flex h-6 min-w-6 sm:h-8 sm:min-w-8 items-center justify-center rounded-lg border-2 px-2 text-[15px] sm:text-[16px] font-bold leading-none shadow-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0253e0] focus-visible:ring-offset-1',
                entry === safePage
                  ? 'border-[#0253e0] bg-white text-[#0253e0] hover:bg-[#e8f1ff]'
                  : 'border-[#cbd5e1] bg-white text-[#64748b] hover:border-[#94a3b8] hover:text-[#334155]'
              )}
            >
              {entry}
            </button>
          )
        )}
      </div>
      <div className="flex min-w-0 justify-end">
        <button
          type="button"
          onClick={() => onChange(safePage + 1)}
          disabled={safePage >= lastPage}
          className={twMerge(
            'flex shrink-0 items-center gap-0.5 rounded-full border px-2 py-0.5 text-[12px] sm:text-[13px] font-semibold transition-colors leading-none',
            safePage >= lastPage
              ? 'pointer-events-none cursor-not-allowed border-transparent text-[#94a3b8] opacity-45'
              : 'border-[#0253e0] bg-white text-[#0253e0] hover:bg-[#e8f1ff]'
          )}
        >
          Sau
          <RightOutlined className="text-[11px] sm:text-[12px]" />
        </button>
      </div>
    </div>
  );
}

const RANK_CONFIG = {
  1: {
    wrapper: 'left-1/2 -translate-x-1/2 top-[2%] w-[28%] h-[60%] z-[35]',
    avatarSize: 'w-[80%]',
    avatarPos: 'left-1/2 -translate-x-1/2 top-[4%]',
    nameTop: 'top-[82%]',
    scoreTop: 'top-[110%]',
    textWidth: 'w-full'
  },
  2: {
    wrapper: 'right-[2.5%] top-[22%] w-[28%] h-[54%]',
    avatarSize: 'w-[75%]',
    avatarPos: 'left-1/2 -translate-x-1/2 top-[4%]',
    nameTop: 'top-[90%]',
    scoreTop: 'top-[122%]',
    textWidth: 'w-full'
  },
  3: {
    wrapper: 'left-[2.5%] top-[31%] w-[28%] h-[50%]',
    avatarSize: 'w-[75%]',
    avatarPos: 'left-1/2 -translate-x-1/2 top-[4%]',
    nameTop: 'top-[95%]',
    scoreTop: 'top-[122%]',
    textWidth: 'w-full'
  }
};

/** Số phiếu + chấm vàng trên bục top 3 — chỉnh min / giữa / max tại đây (đồng bộ cả hai). */
const PODIUM_VOTE_SIZE = 'clamp(12px, 1.1vw + 4px, 12px)';

const norm = (s) => String(s ?? '').trim().toLowerCase();

/** BXH có thể dùng `code` làm id, catalog đội dùng id khác — khớp không phân biệt hoa thường / theo tên */
const resolveTeamFromLeaderboardRow = (row, championTeams) => {
  if (!row?.id) return null;
  const rid = String(row.id).trim();
  const byId = championTeams.find((t) => norm(t.id) === norm(rid));
  if (byId) return byId;
  const rn = norm(row.name);
  if (!rn) return null;
  return championTeams.find((t) => norm(t.name) === rn) || null;
};

const resolvePlayerFromLeaderboardRow = (row, players) => {
  if (!row?.id) return null;
  const rid = String(row.id).trim();
  const byId = players.find((p) => norm(p.id) === norm(rid));
  if (byId) return byId;
  const rn = norm(row.name);
  if (!rn) return null;
  return players.find((p) => norm(p.name) === rn) || null;
};

const LeaderboardPanel = ({ top3, rows, resolvePhoto, onPickRow }) => {
  const podium = [{ rank: 3, ...top3[2] }, { rank: 1, ...top3[0] }, { rank: 2, ...top3[1] }].filter(
    (x) => x?.id
  );
  const pickable = typeof onPickRow === 'function';
  return (
    <div className="flex h-full min-h-0 w-full flex-col lg:max-w-[325px] rounded-2xl p-4">
      <div className="relative mx-auto w-full max-w-[620px] shrink-0">
        <img
          src="/assets/images/tab/top.png"
          alt="top podium"
          className="w-full h-auto object-contain select-none pointer-events-none opacity-0"
          draggable={false}
        />
        <img
          src="/assets/images/tab/top.png"
          alt=""
          aria-hidden
          className="absolute inset-0 z-[30] w-full h-full object-contain select-none pointer-events-none"
          draggable={false}
        />
        {podium.map((item) => {
          const cfg = RANK_CONFIG[item.rank];
          const photo = resolvePhoto(item);
          const inner = (
            <>
              <div
                className={twMerge(
                  'absolute z-[20] rounded-full border-[2px] border-white shadow-[0_2px_8px_rgba(0,0,0,0.25)] overflow-hidden aspect-square bg-white',
                  cfg.avatarSize,
                  cfg.avatarPos
                )}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt=""
                    className="block h-full w-full origin-center scale-[1.08] object-cover object-center"
                  />
                ) : (
                  <div className="w-full h-full bg-[#f3f4f6]" />
                )}
              </div>
              <div
                className={twMerge(
                  'absolute z-[40] left-1/2 -translate-x-1/2 text-center text-white px-0.5 max-w-[98%]',
                  cfg.nameTop,
                  cfg.textWidth
                )}
              >
                <div
                  className="font-black drop-shadow-[0_2px_2px_rgba(0,0,0,0.35)] w-full text-center line-clamp-2 leading-[1.12]"
                  style={{ fontSize: 'clamp(12px, 0.55vw + 3px, 12px)' }}
                >
                  {item.name}
                </div>
              </div>
              <div
                className={twMerge(
                  'absolute z-[40] left-1/2 -translate-x-1/2 flex items-center justify-center gap-0.5 text-white px-0.5',
                  cfg.scoreTop,
                  cfg.textWidth
                )}
              >
                <span
                  style={{
                    width: PODIUM_VOTE_SIZE,
                    height: PODIUM_VOTE_SIZE
                  }}
                  className="rounded-full bg-[#ffd24d] border border-[#f0b400] shrink-0"
                />
                <span
                  style={{
                    fontSize: PODIUM_VOTE_SIZE,
                    minHeight: PODIUM_VOTE_SIZE
                  }}
                  className="inline-flex items-center font-black drop-shadow-[0_2px_2px_rgba(0,0,0,0.35)] leading-none"
                >
                  {Intl.NumberFormat('vi-VN').format(item.votes || 0)}
                </span>
              </div>
            </>
          );
          if (pickable) {
            return (
              <button
                key={`${item.rank}-${item.id}`}
                type="button"
                title="Chọn để điền vào ô dự đoán"
                onClick={() => onPickRow(item)}
                className={twMerge(
                  'absolute border-0 bg-transparent p-0 cursor-pointer outline-none transition-opacity hover:opacity-95 focus-visible:ring-2 focus-visible:ring-[#0253e0] focus-visible:ring-offset-1',
                  cfg.wrapper
                )}
              >
                {inner}
              </button>
            );
          }
          return (
            <div key={`${item.rank}-${item.id}`} className={twMerge('absolute', cfg.wrapper)}>
              {inner}
            </div>
          );
        })}
      </div>

      <div
        className="mt-4 flex min-h-0 flex-1 flex-col rounded-xl border border-[#b8d4f8] p-2"
        style={{
          background: 'linear-gradient(180deg, #DCE9F9 0%, #3580FF 100%)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4)'
        }}
      >
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden [-webkit-overflow-scrolling:touch]">
        {rows.slice(3, 10).map((row, idx) => {
          const order = idx + 4;
          const rowPhoto = resolvePhoto(row);
          return (
            <div
              key={`${row.id}-${order}`}
              role={pickable ? 'button' : undefined}
              tabIndex={pickable ? 0 : undefined}
              onClick={pickable ? () => onPickRow(row) : undefined}
              onKeyDown={
                pickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPickRow(row);
                      }
                    }
                  : undefined
              }
              title={pickable ? 'Chọn để điền vào ô dự đoán' : undefined}
              className={twMerge(
                'flex items-center justify-between px-3 py-2 text-sm rounded-[10px] border mb-2 last:mb-0 bg-white border-[#d7e3f4] shadow-sm',
                pickable
                  ? 'cursor-pointer transition-colors hover:bg-[#f0f7ff] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0253e0]'
                  : ''
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 text-[#6b7280] ">{order}</div>
                <div className="relative h-7 w-7 shrink-0">
                  <div className="absolute inset-0 overflow-hidden rounded-full bg-white">
                    {rowPhoto ? (
                      <img
                        src={rowPhoto}
                        alt=""
                        className="block h-full w-full origin-center scale-[1.14] object-cover object-center"
                      />
                    ) : null}
                  </div>
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-full border border-[#e5e7eb]"
                    aria-hidden
                  />
                </div>
                <div className=" text-[#374151] truncate">{row.name}</div>
              </div>
              <div className="text-[#374151]">{Intl.NumberFormat('vi-VN').format(row.votes || 0)}</div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
};

function VotePanel({
  title,
  query,
  onQueryChange,
  onSubmit,
  loading,
  items,
  selectedId,
  onSelect,
  emptyText,
  showTeamName,
  getItemPhoto,
  searchPlaceholder = 'Tìm kiếm',
  countryFilter = 'all',
  onCountryChange,
  countryOptions = [{ value: 'all', label: 'Tất cả' }],
  countryLabel = 'Quốc gia',
  /** Danh sách đầy đủ từ API để gợi ý khi focus ô tìm (không dùng list đã lọc theo chữ) */
  catalogItems,
  catalogKind = 'team',
  suggestionGroupLabel = 'Danh sách'
}) {
  const [page, setPage] = useState(1);
  const lastPage = Math.max(1, Math.ceil(items.length / VOTE_GRID_PAGE_SIZE));

  const catalogSource = catalogItems?.length ? catalogItems : items;

  const suggestionFiltered = useMemo(() => {
    const byCountry = filterCatalogByCountry(catalogSource, countryFilter, catalogKind);
    const byQuery = filterCatalogByQuery(byCountry, query);
    return byQuery.slice(0, SUGGESTION_MAX);
  }, [catalogSource, countryFilter, catalogKind, query]);

  const catalogForSuggest = useMemo(
    () => filterCatalogByCountry(catalogSource, countryFilter, catalogKind),
    [catalogSource, countryFilter, catalogKind]
  );

  const autoCompleteOptions = useMemo(() => {
    if (!suggestionFiltered.length) return [];
    return [
      {
        label: suggestionGroupLabel,
        options: suggestionFiltered.map((t) => ({
          value: `${VOTE_AC_PREFIX}${t.id}`,
          label:
            catalogKind === 'player' && showTeamName && t.teamName ? (
              <div className="flex flex-col gap-0">
                <span className="text-[13px] font-semibold text-[#111827]">{t.name}</span>
                <span className="text-[10px] text-[#9ca3af]">{t.teamName}</span>
              </div>
            ) : (
              <span className="text-[13px] font-semibold text-[#111827]">{t.name}</span>
            )
        }))
      }
    ];
  }, [suggestionFiltered, suggestionGroupLabel, catalogKind, showTeamName]);

  const handleSearchInputChange = (v) => {
    const s = v ?? '';
    if (typeof s === 'string' && s.startsWith(VOTE_AC_PREFIX)) {
      const id = s.slice(VOTE_AC_PREFIX.length);
      const hit = catalogForSuggest.find((x) => String(x.id) === String(id));
      if (hit) {
        onQueryChange(hit.name);
        onSelect(hit);
      }
      return;
    }
    onQueryChange(s);
  };

  // Chỉ reset trang khi đổi bộ lọc quốc gia — không phụ thuộc `query`/`items.length`
  // (chọn item ở trang 2+ sẽ điền ô tìm và có thể đổi độ dài list; không được nhảy về trang 1).
  // Khi list ngắn lại (gõ tìm), effect `page > lastPage` bên dưới sẽ kẹp trang hợp lệ.
  useEffect(() => {
    setPage(1);
  }, [countryFilter]);

  useEffect(() => {
    if (page > lastPage) setPage(lastPage);
  }, [page, lastPage]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * VOTE_GRID_PAGE_SIZE;
    return items.slice(start, start + VOTE_GRID_PAGE_SIZE);
  }, [items, page]);

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col lg:max-w-[720px] rounded-2xl p-4">
      <div className="flex shrink-0 items-center justify-center w-full">
        <div
          className="w-full max-w-[min(100%,680px)] mx-auto rounded-lg px-3 py-2.5 text-center border border-[#c5ddf5]"
          style={{
            background: 'linear-gradient(180deg, #f8fbff 0%, #e8f2ff 50%, #f0f7ff 100%)'
          }}
        >
          {/* Fill Figma: radial (nhẹ) + linear + nền #0053DF; stroke trắng — qua background-clip + text-stroke */}
          <h2
            className="font-black uppercase text-[17px] min-[640px]:text-[22px] lg:text-[25px] tracking-tight leading-tight inline-block max-w-full [paint-order:stroke_fill]"
            style={{
              backgroundImage: `radial-gradient(ellipse 95% 90% at 50% -35%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0) 55%), linear-gradient(180deg, #3d8fff 0%, #0053DF 42%, #0047c4 100%)`,
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              textShadow: '0 1px 2px rgba(0,83,223,0.15)'
            }}
          >
            {title} <span className="whitespace-nowrap">World Cup 2026</span>
          </h2>
        </div>
      </div>

      <div className="mt-3 w-full max-w-[720px] mx-auto shrink-0">
        {/* Dùng min-[640px]: vì tailwind.config ghi đè `sm` = 400px — không trùng breakpoint chuẩn */}
        <div className="flex w-full flex-col gap-2 min-[640px]:flex-row min-[640px]:flex-nowrap min-[640px]:items-center min-[640px]:gap-3">
          <div className="hidden flex h-[32px] lg:h-[38px] w-full shrink-0 items-center gap-1 rounded-full border border-[#e5e7eb] bg-white px-2 pl-2.5 shadow-sm min-[640px]:w-auto min-[640px]:max-w-[min(260px,40vw)] min-[640px]:gap-1.5 min-[640px]:pl-3">
            <GlobalOutlined className="shrink-0 text-[14px] text-[#2563eb] lg:text-[15px]" />
            <span className="text-[11px] text-[#64748b] min-[640px]:text-[12px] lg:text-[13px] whitespace-nowrap">
              {countryLabel}:
            </span>
            <Select
              variant="borderless"
              popupMatchSelectWidth={false}
              value={countryFilter}
              onChange={onCountryChange}
              options={countryOptions}
              className="min-w-0 flex-1 text-left h-full [&_.ant-select-selector]:!h-full [&_.ant-select-selector]:!min-h-0 [&_.ant-select-selector]:!py-0 [&_.ant-select-selector]:border-0 [&_.ant-select-selector]:bg-transparent [&_.ant-select-selector]:px-0 [&_.ant-select-selector]:shadow-none [&_.ant-select-selection-item]:!px-0 [&_.ant-select-selection-item]:!leading-none [&_.ant-select-selection-item]:!font-bold [&_.ant-select-selection-item]:!text-[#2563eb] [&_.ant-select-selection-placeholder]:!text-[#2563eb]"
              suffixIcon={<DownOutlined className="text-[10px] text-[#2563eb]" />}
            />
          </div>
          <div className="flex w-full min-w-0 items-center gap-2 min-[640px]:contents">
            <AutoComplete
              value={query}
              options={autoCompleteOptions}
              onChange={handleSearchInputChange}
              filterOption={false}
              allowClear
              className="min-w-0 flex-1 min-[640px]:min-w-0 min-[640px]:flex-1"
              popupClassName="min-w-[min(100%,min(360px,92vw))] max-h-[min(320px,50vh)] [&_.ant-select-item]:!py-2"
              popupMatchSelectWidth={false}
              notFoundContent={catalogForSuggest.length ? 'Không có kết quả' : 'Đang tải danh sách...'}
            >
              <Input
                placeholder={searchPlaceholder}
                suffix={<SearchOutlined className="text-[#94a3b8] text-[14px]" />}
                className="h-[32px] lg:h-[38px] rounded-full border border-[#e5e7eb] bg-white text-[13px] lg:text-[15px] text-[#64748b] shadow-sm placeholder:text-[#9aa9c0] [&_input]:py-0"
                styles={{ input: { backgroundColor: '#ffffff' } }}
              />
            </AutoComplete>
            <Button
              type="primary"
              className="h-[32px] lg:h-[38px] shrink-0 rounded-full px-3 min-[640px]:px-5 font-extrabold text-[12px] lg:text-[15px] leading-none border border-[#0147b8] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] min-w-[88px] min-[640px]:min-w-[108px]"
              style={{
                background: 'linear-gradient(180deg, #1a6cf0 0%, #0253e0 100%)',
                color: '#fff'
              }}
              loading={loading}
              onClick={onSubmit}
            >
              Dự đoán
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 flex min-h-0 flex-1 flex-col rounded-2xl p-[2px] outline outline-2 outline-[#A6CAFA]">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px]">
          <div className="flex min-h-0 flex-1 flex-col px-4 pt-4 pb-1 sm:px-5 sm:pt-5">
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pb-3 scrollbar-thin-gray [-webkit-overflow-scrolling:touch]">
              <div className="grid w-full justify-items-stretch [grid-template-columns:repeat(3,minmax(0,1fr))] min-[520px]:[grid-template-columns:repeat(4,minmax(0,1fr))] md:[grid-template-columns:repeat(5,minmax(0,1fr))] gap-x-3 gap-y-3 sm:gap-x-4 sm:gap-y-4">
                {paginatedItems.map((item) => {
                  const isActive = String(selectedId) === String(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => onSelect(item)}
                      className={twMerge(
                        'relative flex h-[117px] w-full min-w-0 shrink-0 flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl border bg-[#f6f9ff] px-2 py-2 transition-all hover:shadow-md',
                        isActive ? 'border-[#0253e0] ring-2 ring-[#0253e0]/25' : 'border-[#c6d7ef]'
                      )}
                    >
                      {isActive ? (
                        <div className="absolute top-0 right-0 w-6 h-6 rounded-bl-lg rounded-tr-lg bg-[#0253e0] flex items-center justify-center">
                          <CheckOutlined className="text-white text-[13px] font-black" />
                        </div>
                      ) : null}
                      <div className="relative h-12 w-12 shrink-0">
                        <div className="absolute inset-0 overflow-hidden rounded-full bg-white">
                          <img
                            src={getItemPhoto(item)}
                            alt=""
                            className="block h-full w-full origin-center scale-[1.22] object-cover object-center"
                            draggable={false}
                          />
                        </div>
                        <div
                          className="pointer-events-none absolute inset-0 z-[1] rounded-full border border-[#e5e7eb]"
                          aria-hidden
                        />
                      </div>
                      <div className="w-full min-w-0">
                        <div className="text-[12px] lg:text-[14px] font-black text-[#111827] text-center leading-tight line-clamp-2">
                          {item.name}
                        </div>
                        {showTeamName && item.teamName ? (
                          <div className="mt-0.5 text-[10px] lg:text-[11px] font-medium text-[#9ca3af] text-center leading-tight line-clamp-1">
                            {item.teamName}
                          </div>
                        ) : null}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="shrink-0 px-4 pb-4 pt-2 sm:px-5">
            <VoteGridPagination
              className="w-full shrink-0"
              current={Math.min(page, lastPage)}
              totalItems={items.length}
              pageSize={VOTE_GRID_PAGE_SIZE}
              onChange={setPage}
            />
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 text-center text-sm text-[#6b7280]">{emptyText}</div>
      ) : null}
    </div>
  );
}

export const ChampionPredictionTab = ({
  championLeaderboard,
  championTeams,
  filteredChampionTeams,
  championQuery,
  setChampionQuery,
  championCountryFilter,
  setChampionCountryFilter,
  championCountryOptions,
  isChampionVoting,
  onSubmitChampion,
  selectedChampionId,
  setSelectedChampionId,
  votePanelTitle = 'Dự đoán đội vô địch',
  searchPlaceholder = 'Tìm kiếm đội tuyển',
  fillChampionQueryFromPick
}) => {
  const top3 = championLeaderboard.slice(0, 3).map((row) => {
    const team = resolveTeamFromLeaderboardRow(row, championTeams);
    return { ...row, flag: row.flag ?? team?.flag };
  });

  const handlePickLeaderboardRow = (row) => {
    if (!row?.id) return;
    const team = resolveTeamFromLeaderboardRow(row, championTeams);
    const idToUse = team ? team.id : row.id;
    setSelectedChampionId(idToUse);
    if (!team?.name && row.name && typeof fillChampionQueryFromPick === 'function') {
      fillChampionQueryFromPick(row.name);
    }
  };

  return (
    <div className="mt-3 grid w-full grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,325px)_minmax(0,720px)] lg:gap-6 lg:justify-center">
      <div className="min-h-0 min-w-0 lg:h-full">
        <LeaderboardPanel
          top3={top3}
          rows={championLeaderboard}
          resolvePhoto={(x) => x.flag}
          onPickRow={handlePickLeaderboardRow}
        />
      </div>
      <div className="flex min-h-0 min-w-0 w-full flex-col lg:h-full">
        <VotePanel
          title={votePanelTitle}
          query={championQuery}
          onQueryChange={setChampionQuery}
          searchPlaceholder={searchPlaceholder}
          countryFilter={championCountryFilter}
          onCountryChange={setChampionCountryFilter}
          countryOptions={championCountryOptions}
          onSubmit={onSubmitChampion}
          loading={isChampionVoting}
          items={filteredChampionTeams}
          selectedId={selectedChampionId}
          onSelect={(team) => setSelectedChampionId(team.id)}
          emptyText="Không tìm thấy đội phù hợp."
          showTeamName={false}
          getItemPhoto={(team) => team.flag}
          catalogItems={championTeams}
          catalogKind="team"
          suggestionGroupLabel="Danh sách đội tuyển"
        />
      </div>
    </div>
  );
};

export const PlayerPredictionTab = ({
  title,
  leaderboard,
  players,
  /** Toàn bộ cầu thủ từ API (gợi ý ô tìm); mặc định dùng `players` */
  catalogPlayers,
  query,
  setQuery,
  countryFilter,
  setCountryFilter,
  countryOptions,
  selectedId,
  setSelectedId,
  loading,
  onSubmit,
  fillPlayerQueryFromPick
}) => {
  const playerCatalog = catalogPlayers?.length ? catalogPlayers : players;

  const top3 = leaderboard.slice(0, 3).map((row) => {
    const pl = resolvePlayerFromLeaderboardRow(row, playerCatalog);
    return { ...row, photo: row.photo ?? row.flag ?? pl?.photo };
  });

  const handlePickLeaderboardRow = (row) => {
    if (!row?.id) return;
    const player = resolvePlayerFromLeaderboardRow(row, playerCatalog);
    const idToUse = player ? player.id : row.id;
    setSelectedId(idToUse);
    if (!player?.name && row.name && typeof fillPlayerQueryFromPick === 'function') {
      fillPlayerQueryFromPick(row.name);
    }
  };

  return (
    <div className="mt-3 grid w-full grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,325px)_minmax(0,720px)] lg:gap-6 lg:justify-center">
      <div className="min-h-0 min-w-0 lg:h-full">
        <LeaderboardPanel
          top3={top3}
          rows={leaderboard}
          resolvePhoto={(row) => {
            const pl = resolvePlayerFromLeaderboardRow(row, playerCatalog);
            return row.photo ?? row.flag ?? pl?.photo;
          }}
          onPickRow={handlePickLeaderboardRow}
        />
      </div>
      <div className="flex min-h-0 min-w-0 w-full flex-col lg:h-full">
      <VotePanel
        title={title}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder="Tìm kiếm cầu thủ"
        countryFilter={countryFilter}
        onCountryChange={setCountryFilter}
        countryOptions={countryOptions}
        onSubmit={onSubmit}
        loading={loading}
        items={players}
        selectedId={selectedId}
        onSelect={(player) => setSelectedId(player.id)}
        emptyText="Không tìm thấy cầu thủ phù hợp."
        showTeamName
        getItemPhoto={(player) => player.photo}
        catalogItems={playerCatalog}
        catalogKind="player"
        suggestionGroupLabel="Danh sách cầu thủ"
      />
      </div>
    </div>
  );
};

